import React, { useEffect } from "react";

// ─── Icons ─────────────────────────────────────────────────────────────────────
const LocationIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
		<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
);

const PhoneIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
		<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.7a16 16 0 0 0 6 6l.9-1.39a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.5 16z" />
	</svg>
);

// ─── Inline styles ─────────────────────────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById("patient-report-header-styles")) return;
	const style = document.createElement("style");
	style.id = "patient-report-header-styles";
	style.textContent = `
		.prh-wrap, .prh-wrap *, .prh-wrap *::before, .prh-wrap *::after {
			box-sizing: border-box;
		}
		.prh-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			overflow-x: hidden;
		}

		/* ── Hero — full-width orange band ── */
		.prh-hero {
			background: linear-gradient(135deg, #F4A96A 0%, #E47029 55%, #B94D1A 100%);
			padding: 20px 28px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 16px;
			position: relative;
			overflow: hidden;
			min-height: 88px;
		}
		.prh-hero::after {
			content: '';
			position: absolute;
			width: 220px; height: 220px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.12);
			right: -60px; top: -80px;
		}
		.prh-hero::before {
			content: '';
			position: absolute;
			width: 130px; height: 130px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.1);
			left: -40px; bottom: -60px;
		}

		/* logo card — sized to the logo's own aspect ratio (height-driven,
		   width auto) so wide/horizontal logos get a wider container and
		   circular/square logos land in a balanced square container. */
		.prh-logo-card {
			border-radius: 12px;
			padding: 10px 18px;
			position: relative;
			z-index: 1;
			flex-shrink: 0;
			transition: transform 0.2s ease, box-shadow 0.2s ease;
			display: flex;
			align-items: center;
			justify-content: center;
			height: 130px;
			width: auto;
			min-width: 130px;
			max-width: 320px;
			background: radial-gradient(
				ellipse at center,
				rgba(255,255,255,0.32) 0%,
				rgba(255,255,255,0.1) 70%,
				rgba(255,255,255,0) 100%
			);
		}
		.prh-logo-card:hover {
			transform: translateY(-2px);
			box-shadow: 0 6px 18px rgba(0,0,0,0.22);
		}
		.prh-logo {
			height: 100%;
			width: auto;
			max-width: 100%;
			object-fit: contain;
			display: block;
		}

		/* powered by pill */
		.prh-powered {
			position: relative;
			z-index: 1;
			display: inline-flex;
			align-items: center;
			gap: 6px;
			font-size: 10px;
			color: rgba(255,255,255,0.82);
			text-transform: uppercase;
			letter-spacing: 0.08em;
			font-weight: 600;
			text-decoration: none;
			padding: 4px 11px;
			border-radius: 20px;
			border: 1px solid rgba(255,255,255,0.25);
			background: rgba(255,255,255,0.12);
			transition: background 0.15s ease, color 0.15s ease;
			white-space: nowrap;
			flex-shrink: 0;
		}
		.prh-powered:hover {
			background: rgba(255,255,255,0.22);
			color: #fff;
		}
		.prh-powered-dot {
			width: 5px; height: 5px;
			border-radius: 50%;
			background: rgba(255,255,255,0.7);
			flex-shrink: 0;
			transition: background 0.15s;
		}
		.prh-powered:hover .prh-powered-dot { background: #fff; }

		/* ── Info bar below hero ── */
		.prh-bar {
			background: #fff;
			display: flex;
			align-items: stretch;
			border-bottom: 1px solid rgba(0,0,0,0.06);
			min-height: 0;
		}

		/* contact chips */
		.prh-chip {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 15px 22px;
			flex: 1;
			position: relative;
			animation: prh-fade-in 0.35s ease both;
			min-width: 0;
		}
		.prh-chip:nth-child(1) { animation-delay: 0.06s; }
		.prh-chip:nth-child(2) { animation-delay: 0.12s; }

		.prh-chip + .prh-chip::before {
			content: '';
			position: absolute;
			left: 0; top: 50%;
			transform: translateY(-50%);
			width: 1px; height: 30px;
			background: rgba(0,0,0,0.07);
		}

		@keyframes prh-fade-in {
			from { opacity: 0; transform: translateY(5px); }
			to   { opacity: 1; transform: translateY(0); }
		}

		.prh-chip-icon {
			width: 36px; height: 36px;
			border-radius: 10px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			border: 1.5px solid #F5D5C0;
			transition: transform 0.2s ease, background 0.2s ease;
		}
		.prh-chip:hover .prh-chip-icon {
			transform: scale(1.07);
			background: #FBCFAC;
		}

		.prh-chip-text {
			min-width: 0;
			flex: 1;
		}
			font-size: 9.5px;
			color: #9A9A9F;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			font-weight: 600;
			margin-bottom: 3px;
		}
		.prh-chip-val {
			font-size: 13px;
			color: #18181A;
			font-weight: 600;
			line-height: 1.2;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			max-width: 260px;
		}

		/* date — right side */
		.prh-date {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			justify-content: center;
			padding: 15px 22px;
			border-left: 1px solid rgba(0,0,0,0.06);
			flex-shrink: 0;
			animation: prh-fade-in 0.35s ease 0.18s both;
		}
		.prh-date-lbl {
			font-size: 9.5px;
			color: #9A9A9F;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			font-weight: 600;
			margin-bottom: 3px;
		}
		.prh-date-val {
			font-size: 13px;
			color: #18181A;
			font-weight: 700;
			white-space: nowrap;
		}

		/* ── Responsive ── */
		@media (max-width: 768px) {
			.prh-logo-card {
				height: 104px;
				min-width: 104px;
				max-width: 260px;
				padding: 8px 16px;
			}
		}

		@media (max-width: 640px) {
			.prh-hero { padding: 14px 18px; flex-wrap: wrap; gap: 10px; }
			.prh-bar { flex-direction: column; }
			.prh-chip { padding: 13px 18px; }
			.prh-chip + .prh-chip::before {
				top: 0; left: 18px; right: 18px;
				width: auto; height: 1px;
				transform: none;
			}
			.prh-date {
				align-items: flex-start;
				padding: 13px 18px;
				border-left: none;
				border-top: 1px dashed rgba(0,0,0,0.08);
			}
		}

		@media (max-width: 480px) {
			.prh-logo-card {
				height: 80px;
				min-width: 80px;
				max-width: 200px;
				padding: 6px 12px;
			}
		}

		@media (max-width: 380px) {
			.prh-powered { font-size: 9px; padding: 3px 8px; }
		}
	`;
	document.head.appendChild(style);
};

// ─── Component ─────────────────────────────────────────────────────────────────
export default function PatientReportHeader({ logo, address, contactNum }) {
	useEffect(() => {
		injectStyles();
	}, []);

	const hasAddress = address && address !== "null" && address !== "";
	const hasPhone   = contactNum && contactNum !== "null" && contactNum !== "";

	const today = new Date().toLocaleDateString("en-CA", {
		weekday: "long",
		year:    "numeric",
		month:   "long",
		day:     "numeric",
	});

	return (
		<div className="prh-wrap">
			<header>

				{/* ── Full-width orange hero ── */}
				<div className="prh-hero">
					<div className="prh-logo-card">
						<img
							className="prh-logo"
							src={logo}
							alt="Clinic logo"
						/>
					</div>
					<a
						className="prh-powered"
						href="https://www.portonhealth.com/"
						target="_blank"
						rel="noopener noreferrer"
					>
						<span className="prh-powered-dot" />
						Powered by Porton
					</a>
				</div>

				{/* ── Info bar — address / phone / date ── */}
				<div className="prh-bar">
					{hasAddress && (
						<div className="prh-chip">
							<div className="prh-chip-icon">
								<LocationIcon />
							</div>
							<div className="prh-chip-text">
								<div className="prh-chip-lbl">Address</div>
								<div className="prh-chip-val">{address}</div>
							</div>
						</div>
					)}

					{hasPhone && (
						<div className="prh-chip">
							<div className="prh-chip-icon">
								<PhoneIcon />
							</div>
							<div className="prh-chip-text">
								<div className="prh-chip-lbl">Phone</div>
								<div className="prh-chip-val">{contactNum}</div>
							</div>
						</div>
					)}

					<div className="prh-date">
						<div className="prh-date-lbl">Today</div>
						<div className="prh-date-val">{today}</div>
					</div>
				</div>

			</header>
		</div>
	);
}